import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, Ref, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';

import Picker, { Option } from 'dashboard/components/FormControlPilledInput/Picker';
import { ControlConfigSelect } from 'dashboard/components/FormControlPilledInput/types';
import { TextValue, ValueType } from 'dashboard/models/graph-query';
import { paragraphMixins } from 'design-system/mixins/text';

interface EditableItemSelectProps {
  controlConfig: ControlConfigSelect;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: Ref<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  onValueChange: (value: TextValue) => void;
  placeholder?: string | null;
  value: TextValue;
  isCompact?: boolean;
}

const EditableSelectInput = styled('input')<{ isCompact: boolean }>`
  ${paragraphMixins.md};
  background: none;
  border: none;

  display: inline;
  min-width: 40px;
  width: unset;
  ${({ isCompact }) => {
    if (isCompact) {
      return `
        padding: unset;
        height: 100%;
      `;
    }
    return `padding: ${Spacing[1]} 0`;
  }};
`;

const EditableItemSelect = ({
  controlConfig,
  deleteItem,
  deleteLastItem,
  inputRef,
  itemKey,
  isLast,
  onValueChange,
  placeholder,
  value: initialValue,
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
}: EditableItemSelectProps) => {
  let initialOption: Option | null = null;
  if (initialValue != null) {
    initialOption = {
      value: initialValue.value,
      label:
        initialValue['raw-value'] === controlConfig.allOption?.value
          ? controlConfig.allOption.label
          : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            controlConfig.options.find((option) => option.value === initialValue['raw-value'])
              .label,
    };
  }

  // The raw value typed by the user.
  const [value, setValue] = useState<string>(initialOption?.label ?? '');

  // The value highlighted by the user, in the picker.
  const [pickerValue, setPickerValue] = useState<Option | null>(initialOption);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const commitValue = (value: string) => {
    if (value.length === 0) {
      if (!isLast) {
        deleteItem();
      }
    } else {
      const option = controlConfig.options.find((option) => {
        return option.label === value;
      });

      if (option) {
        const processedValue = {
          ...initialValue,
          'raw-value': option.value,
          value: option.value,
          type: ValueType.Text,
        } as TextValue;

        onValueChange(processedValue);
      } else {
        // invalid option
        if (!isLast) {
          deleteItem();
        }
      }

      // TODO: See if we can remove this by changing 'defaultValue' and
      // making use of useEffect
      setValue('');
    }
  };

  const handleKeyDown =
    (pickerUp: () => void, pickerDown: () => void) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        pickerDown();
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        pickerUp();
      }

      if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
        e.preventDefault();
        if (pickerValue != null) {
          setValue(pickerValue.label);
          commitValue(pickerValue.label);
        }
      }

      if (e.key === 'Backspace' && value.trim() === '') {
        if (isLast) {
          deleteLastItem();
        } else {
          deleteItem();
        }
      }
    };

  const handleFocus =
    (setPickerActive: (value: boolean) => void) => (_e: FocusEvent<HTMLInputElement>) => {
      setPickerActive(true);
    };

  const handlePickerValueChange = (option: Option | null) => {
    setPickerValue(option);
  };

  const handlePickerClick = (option: Option, _e: MouseEvent<HTMLLIElement>) => {
    commitValue(option.label);
  };

  const handleOutsideClick = () => {
    commitValue(value);
  };

  return (
    <OutsideClickHandler display='inline-block' onOutsideClick={handleOutsideClick}>
      <Picker
        options={controlConfig.options}
        onValueChange={handlePickerValueChange}
        onPickerClick={handlePickerClick}
        searchTerm={value}
        render={({ setPickerActive, pickerUp, pickerDown }) => (
          <EditableSelectInput
            key={itemKey}
            size={value?.length || placeholder?.length || 3}
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown(pickerUp, pickerDown)}
            onFocus={handleFocus(setPickerActive)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            placeholder={placeholder}
            isCompact={isCompact}
          />
        )}
      />
    </OutsideClickHandler>
  );
};

export default EditableItemSelect;
